import { useState } from 'react';
import { useHistory } from "react-router-dom";
import { useFormik } from 'formik';
import * as yup from 'yup';

/* Styles */
import styles from 'styles/login.module.scss';
import layoutStyles from 'styles/layout.module.scss';
import onboardingStyles from 'styles/onboarding.module.scss';
import cn from 'classnames';

/* i18n */
import { useIntl } from 'react-intl';

/* API */
import { postID } from 'lib/API';

/* Redux */
import { useDispatch } from 'react-redux';
import { setNeedsContactInfo, setPreferredName } from 'app/slices/appSlice';

/* Material UI and other UI Dependencies */
import Typography from "@material-ui/core/Typography";
import OnboardingSteps from 'components/OnboardingSteps';
import {
  TextField,
  Button,
  Box,
  FormControl,
  FormControlLabel,
  Checkbox,
  FormHelperText
} from "@material-ui/core";
import PhoneNumberInput from 'components/inputs/PhoneNumberInput';
import DOBInput from 'components/inputs/DOBInput';
import SSNInput from 'components/inputs/SSNInput';

/* Analytics */
import { trackEvent } from 'lib/analytics';

export default function OnboardingContact() {
  const [submitting, setSubmitting] = useState(false);
  const [contactError, setContactError] = useState(false);

  const dispatch = useDispatch();
  const intl = useIntl();
  const urlPrefix = intl.locale === 'en' ? '' : `/${intl.locale}`;
  const history = useHistory();

  const phoneRegExp = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/;

  const validationSchema = yup.object({
    first_name: yup
        .string( intl.formatMessage({id:'forms.onboarding.firstNameValidation', description:'string'}))
        .min(2, intl.formatMessage({id:'forms.onboarding.firstNameMinLength', description:'string'}))
        .max(50, intl.formatMessage({id:'forms.onboarding.firstNameMaxLength', description:'string'}))
        .required( intl.formatMessage({id:'forms.onboarding.firstNameRequired', description:'string'})),
    last_name: yup
        .string( intl.formatMessage({id:'forms.onboarding.lastNameValidation', description:'string'}))
        .min(2, intl.formatMessage({id:'forms.onboarding.lastNameMinLength', description:'string'}))
        .max(50, intl.formatMessage({id:'forms.onboarding.lastNameMaxLength', description:'string'}))
        .required( intl.formatMessage({id:'forms.onboarding.lastNameRequired', description:'string'})),
    phone_number: yup
        .string( intl.formatMessage({id:'forms.onboarding.phoneNumber', description:'string'}))
        .matches( phoneRegExp, intl.formatMessage({id:'forms.onboarding.phoneNumberValidation', description:'string'}))
        .required( intl.formatMessage({id:'forms.onboarding.phoneNumberRequired', description:'string'})),
    dob: yup
        .date( intl.formatMessage({id:'forms.onboarding.dateOfBirthValidation', description:'string'}))
        .typeError( intl.formatMessage({id:'forms.onboarding.dateOfBirthInvalid', description:'string'}))
        .min( "01/01/1900", intl.formatMessage({id:'forms.onboarding.dateOfBirthTooOld', description:'string'}))
        .max( new Date(), intl.formatMessage({id:'forms.onboarding.dateOfBirthTooNew', description:'string'}))
        .required( intl.formatMessage({id:'forms.onboarding.dateOfBirthRequired', description:'string'})),
    ssnlast4: yup
        .string( intl.formatMessage({id:'forms.onboarding.socialSecurityNumber', description:'string'}))
        .matches(/^[0-9]+$/, intl.formatMessage({id:'forms.onboarding.onlyDigits', description:'string'}))
        .min(4, intl.formatMessage({id:'forms.onboarding.exactlyXDigits', description:'string', }, {amount:4}))
        .max(4, intl.formatMessage({id:'forms.onboarding.exactlyXDigits', description:'string'}, {amount:4}))
        .required( intl.formatMessage({id:'forms.onboarding.socialSecurityNumberRequired', description:'string'})),
    tos: yup
      .bool()
      .oneOf([true], intl.formatMessage({id:'forms.onboarding.tosError', description:'string'}))
  });

  const initialValues = {
    first_name: '',
    last_name: '',
    phone_number: '',
    dob: '',
    ssnlast4: '',
    tos: false
  }

  const formik = useFormik({
    initialValues,
    validationSchema: validationSchema,
      onSubmit: async (values) => {
        trackEvent('ONBOARDING_CONTACT_NEXT');
        setContactError('');
        setSubmitting(true);
        postID(values).then(function(response) {
          dispatch(setNeedsContactInfo(false));
          dispatch(setPreferredName(values.first_name));
          setSubmitting(false);
          history.push(`${urlPrefix}/onboarding/resume-upload`);
        }).catch(function() {
          setSubmitting(false);
        })
    },
  });

  return (
    <>
      <div className={styles.loginFlow__form__onboarding}>
        <Box px={3}>
          <Typography align="center" variant="h1"  gutterBottom>
            {intl.formatMessage({id:'onboarding.contact.title',description:'string'})}
          </Typography>
        </Box>
        <OnboardingSteps activeStep={0} />
      </div>
      <hr className={layoutStyles.separatorBold} />
      <div className={styles.loadingScreen__cardsContainer}>
        <form onSubmit={formik.handleSubmit} className={cn(styles.loginFlow__form, styles.signInFormNoStripe, onboardingStyles.formContainer)}>
          <TextField
            FormHelperTextProps={{
              className: styles.helperText
            }}
            error={formik.touched.first_name && Boolean(formik.errors.first_name)}
            fullWidth
            helperText={formik.touched.first_name && formik.errors.first_name}
            label={intl.formatMessage({id:'forms.onboarding.firstName'})}
            name="first_name"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            type="text"
            value={formik.values.first_name}
            variant="filled"
            inputProps={{
              title: intl.formatMessage({id:'forms.onboarding.firstName'})
            }}
          />
          <TextField
            FormHelperTextProps={{
              className: styles.helperText
            }}
            error={formik.touched.last_name && Boolean(formik.errors.last_name)}
            fullWidth
            helperText={formik.touched.last_name && formik.errors.last_name}
            label={intl.formatMessage({id:'forms.onboarding.lastName'})}
            name="last_name"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            type="text"
            value={formik.values.last_name}
            variant="filled"
            inputProps={{
              title: intl.formatMessage({id:'forms.onboarding.lastName'})
            }}
          />
          <TextField
            FormHelperTextProps={{
              className: styles.helperText
            }}
            error={formik.touched.phone_number && Boolean(formik.errors.phone_number)}
            name="phone_number"
            type="text"
            variant="filled"
            fullWidth
            helperText={formik.touched.phone_number && formik.errors.phone_number}
            autoComplete="off"
            label={intl.formatMessage({id:'forms.onboarding.phoneNumber'})}
            value={formik.values.phone_number}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            InputProps={{
              inputComponent: PhoneNumberInput
            }}
            inputProps={{
              title: intl.formatMessage({id:'forms.onboarding.phoneNumber'})
            }}
          />
          <TextField
            FormHelperTextProps={{
              className: styles.helperText
            }}
            error={formik.touched.dob && Boolean(formik.errors.dob)}
            name="dob"
            type="text"
            variant="filled"
            fullWidth
            helperText={formik.touched.dob && formik.errors.dob}
            autoComplete="off"
            label={intl.formatMessage({id:'forms.onboarding.dateOfBirth'})}
            value={formik.values.dob}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            InputProps={{
              inputComponent: DOBInput
            }}
            inputProps={{
              title: intl.formatMessage({id:'forms.onboarding.dateOfBirth'})
            }}
          />
          <TextField
            FormHelperTextProps={{
              className: styles.helperText
            }}
            error={formik.touched.ssnlast4 && Boolean(formik.errors.ssnlast4)}
            name="ssnlast4"
            type="text"
            variant="filled"
            fullWidth
            helperText={formik.touched.ssnlast4 && formik.errors.ssnlast4}
            autoComplete="off"
            label={intl.formatMessage({id:'forms.onboarding.socialSecurityNumber'})}
            value={formik.values.ssnlast4}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            InputProps={{
              inputComponent: SSNInput
            }}
            inputProps={{
              title: intl.formatMessage({id:'forms.onboarding.socialSecurityNumber'})
            }}
          />
          {contactError.length > 0 && (
              <div className={styles.errorMessage__container}>
                  <label className={styles.errorMessage}>{contactError}</label>
              </div>
          )}
          <div className={onboardingStyles.tosContainer}>
          <FormControl
            classes={{
              root: onboardingStyles.formControlRoot
            }}
          >
            <FormControlLabel
              classes={{
                root: onboardingStyles.formControlLabelRoot,
                label: onboardingStyles.formControlLabeLabel
              }}
              label={intl.formatMessage({id:'forms.onboarding.tos', description:'string'})}
              control={
                <Checkbox
                  value={formik.values.tos} color="primary"
                  onChange={e => formik.handleChange({target: {name: 'tos', value: e.currentTarget.checked}})} name="tos"
                />
              }
              labelPlacement="end"
            />
            <a rel="noreferrer" className={onboardingStyles.tosLink} href={`/#${urlPrefix}/terms-of-use`} target="_blank">{intl.formatMessage({id:'forms.onboarding.tosLink', description:'string'})}</a>
            {formik.touched.tos && formik.errors.tos && (
              <FormHelperText classes={{root: onboardingStyles.tosHelperTextRoot}}>{formik.errors.tos}</FormHelperText>
            )}
          </FormControl>
          </div>
          <div className={styles.submitBtn__container}>
            <Button
              color="secondary"
              disabled={submitting}
              size="large"
              type="submit"
              variant="contained"
            >
              {submitting ? (
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">
                    {intl.formatMessage({id:'forms.signIn.loading', description:'string'})}
                  </span>
                </div>
              ) : (
                intl.formatMessage({id:'forms.onboarding.next', description:'string'})
              )}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
}